import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Alert, AlertDescription } from '../ui/alert';
import { 
  Loader2, 
  CheckCircle, 
  XCircle, 
  Clock,
  ArrowRight,
  AlertCircle
} from 'lucide-react';
import { AsyncJobStatus, jobService } from '../../services/job';
import { jobHistoryManager, JobHistoryEntry } from '../../utils/jobHistory';

interface AsyncJobMonitorProps {
  jobId: string;
  onJobCompleted: (batchId: string) => void;
  onBack: () => void;
  // Optional job metadata for history tracking
  jobMetadata?: {
    storeId: string;
    storeName: string;
    productCount: number;
  };
}

export function AsyncJobMonitor({ jobId, onJobCompleted, onBack, jobMetadata }: AsyncJobMonitorProps) {
  const [jobStatus, setJobStatus] = useState<AsyncJobStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!jobId) return;

    // Add job to history on first load
    if (jobMetadata) {
      const jobName = `${jobMetadata.storeName} - ${jobMetadata.productCount} products`;
      jobHistoryManager.addJob({
        id: jobId,
        name: jobName,
        storeId: jobMetadata.storeId,
        storeName: jobMetadata.storeName,
        productCount: jobMetadata.productCount,
      });
    }

    const pollJobStatus = async () => {
      try {
        const response = await jobService.getAsyncJobStatus(jobId);
        setJobStatus(response);
        setError(null);

        // If job is completed successfully, extract batch_id and transition
        if (response.data.status === 'SUCCESS' && response.data.result) {
          const { created_jobs, total_products, batch_ids } = response.data.result;
          
          if (created_jobs > 0) {
            console.log('Async job completed successfully', response.data.result);
            
            // Update job history with completion
            jobHistoryManager.updateJobStatus(jobId, 'completed', {
              batchId: batch_ids && batch_ids.length > 0 ? batch_ids[0] : undefined
            });
            
            // Use the first batch ID for monitoring (multiple batches might exist for large product sets)
            const batchId = batch_ids && batch_ids.length > 0 ? batch_ids[0] : null;
            
            if (batchId) {
              setTimeout(() => {
                onJobCompleted(batchId);
              }, 1000);
            } else {
              console.warn('No batch ID found in result, cannot transition to monitoring');
            }
          }
        } else if (response.data.status === 'FAILURE') {
          // Update job history with failure
          const failureMessage = response.data.result?.failed_products?.join(', ') || 
                                response.data.error || 
                                'Unknown error';
          jobHistoryManager.updateJobStatus(jobId, 'failed', {
            failedReason: failureMessage
          });
        }
      } catch (err: any) {
        const errorMessage = err.message || 'Failed to get job status';
        setError(errorMessage);
        
        // Update job history with failure
        jobHistoryManager.updateJobStatus(jobId, 'failed', {
          failedReason: errorMessage
        });
      } finally {
        setLoading(false);
      }
    };

    // Initial load
    pollJobStatus();

    // Poll every 2 seconds while job is in progress
    const interval = setInterval(() => {
      if (jobStatus?.data.status === 'PENDING' || jobStatus?.data.status === 'PROGRESS') {
        pollJobStatus();
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [jobId, jobStatus?.data.status, onJobCompleted, jobMetadata]);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'PENDING':
        return <Badge variant="secondary"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
      case 'PROGRESS':
        return <Badge variant="default"><Loader2 className="w-3 h-3 mr-1 animate-spin" />Processing</Badge>;
      case 'SUCCESS':
        return <Badge variant="default" className="bg-green-600 text-white hover:bg-green-700"><CheckCircle className="w-3 h-3 mr-1" />Completed</Badge>;
      case 'FAILURE':
        return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" />Failed</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getProgressPercentage = () => {
    if (!jobStatus?.data.progress) return 0;
    
    const { current = 0, total = 100 } = jobStatus.data.progress;
    return total > 0 ? Math.round((current / total) * 100) : 0;
  };

  if (loading && !jobStatus) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin mr-2" />
            <span>Loading job status...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <XCircle className="w-5 h-5 mr-2 text-red-500" />
            Error Monitoring Job
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
          <div className="mt-4">
            <Button onClick={onBack} variant="outline">
              Back to Jobs
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!jobStatus) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center py-8">
            <p>No job status available</p>
            <Button onClick={onBack} variant="outline" className="mt-4">
              Back to Jobs
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  const { data } = jobStatus;
  const progress = data.progress;
  const result = data.result;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Creating Image Processing Jobs</span>
            {getStatusBadge(data.status)}
          </CardTitle>
          <div className="mt-2 p-2 bg-muted rounded-md">
            <div className="text-sm text-muted-foreground">Job ID (save this to resume monitoring later):</div>
            <div className="font-mono text-sm bg-background px-2 py-1 rounded border mt-1 select-all">
              {jobId}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Progress Information */}
            {progress && (
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Progress</span>
                  <span>{progress.current || 0} / {progress.total || 0} products</span>
                </div>
                <Progress value={getProgressPercentage()} className="w-full" />
                {progress.status && (
                  <p className="text-sm text-muted-foreground">{progress.status}</p>
                )}
              </div>
            )}

            {/* Current Status */}
            {data.status === 'PENDING' && (
              <div className="flex items-center text-sm text-muted-foreground">
                <Clock className="w-4 h-4 mr-2" />
                Job is waiting to be processed...
              </div>
            )}

            {data.status === 'PROGRESS' && progress && (
              <div className="bg-blue-50 dark:bg-blue-950/30 p-3 rounded-lg">
                <div className="text-sm font-medium">Processing in progress</div>
                {progress.created_jobs !== undefined && (
                  <div className="text-sm text-muted-foreground">
                    Created: {progress.created_jobs} jobs
                    {progress.failed_count !== undefined && progress.failed_count > 0 && (
                      <span className="text-red-600 dark:text-red-400 ml-2">Failed: {progress.failed_count}</span>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Success Result */}
            {data.status === 'SUCCESS' && result && (
              <div className="bg-green-50 dark:bg-green-950/30 p-4 rounded-lg">
                <div className="flex items-center mb-2">
                  <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 mr-2" />
                  <span className="font-medium text-green-800 dark:text-green-200">Job Creation Completed</span>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Total Products:</span>
                    <span className="ml-2 font-medium">{result.total_products}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Jobs Created:</span>
                    <span className="ml-2 font-medium text-green-600 dark:text-green-400">{result.created_jobs}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Success Rate:</span>
                    <span className="ml-2 font-medium">{result.success_rate}%</span>
                  </div>
                  {result.failed_products && result.failed_products.length > 0 && (
                    <div>
                      <span className="text-muted-foreground">Failed:</span>
                      <span className="ml-2 font-medium text-red-600 dark:text-red-400">{result.failed_products.length}</span>
                    </div>
                  )}
                </div>
                
                {result.created_jobs > 0 && (
                  <div className="mt-4 p-3 bg-background rounded border">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Ready for processing</span>
                      <Button 
                        size="sm" 
                        onClick={() => {
                          const batchIds = result.batch_ids;
                          const batchId = batchIds && batchIds.length > 0 ? batchIds[0] : null;
                          if (batchId) {
                            onJobCompleted(batchId);
                          }
                        }}
                        className="ml-2"
                        disabled={!result.batch_ids || result.batch_ids.length === 0}
                      >
                        View Jobs <ArrowRight className="w-3 h-3 ml-1" />
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Jobs have been created and are ready for image processing
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Failure Result */}
            {data.status === 'FAILURE' && data.error && (
              <div className="bg-red-50 dark:bg-red-950/30 p-4 rounded-lg">
                <div className="flex items-center mb-2">
                  <XCircle className="w-5 h-5 text-red-600 dark:text-red-400 mr-2" />
                  <span className="font-medium text-red-800 dark:text-red-200">Job Creation Failed</span>
                </div>
                <p className="text-sm text-red-700 dark:text-red-300">{data.error}</p>
              </div>
            )}
          </div>

          <div className="flex justify-between mt-6">
            <Button onClick={onBack} variant="outline">
              Back to Jobs
            </Button>
            
            {data.status === 'SUCCESS' && result && result.created_jobs > 0 && (
              <Button 
                onClick={() => {
                  const batchIds = result.batch_ids;
                  const batchId = batchIds && batchIds.length > 0 ? batchIds[0] : null;
                  if (batchId) {
                    onJobCompleted(batchId);
                  }
                }}
                disabled={!result.batch_ids || result.batch_ids.length === 0}
              >
                Continue to Processing
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}